'use client';

import { css } from '@bts-web/utils-style-engine';
import {
  defaultLanguage,
  getLanguageFromLocale,
  getPluralTranslationKey,
  interpolateTranslation,
  Locale,
  useClientTranslation,
} from '@bts-web/utils-lokalise';
import { SavingsPlansFilterBar } from '../SavingsPlansFilterBar/SavingsPlansFilterBar.use-client';
import { SavingsPlansList } from '../SavingsPlansList/SavingsPlansList';
import { SavingsPlansListTranslationsKeys } from '../types';
import { getAssetTradeUrl } from '../../route-builders';
import {
  SavingsPlansHeaderSkeleton,
  SavingsPlansItemsSkeleton,
} from '../SavingsPlansLoadingSkeleton/SavingsPlansLoadingSkeleton';
import { useSavingsPlansContext } from '../SavingsPlansContext/SavingsPlansContext.use-client';
import { HyperLinkButtonBase } from '@bts-web/core-features/common';

const titleContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small',
  paddingX: 'medium',
  '& > h1': {
    color: 'neutrals.text_primary',
    fontSize: 'headline.large_semi_bold',
    fontWeight: 'headline.large_semi_bold',
    lineHeight: 'headline.large_semi_bold',
    letterSpacing: 'headline.large_semi_bold',
  },
  '& > span': {
    color: 'neutrals.text_secondary',
    fontSize: 'body.medium_medium',
    fontWeight: 'body.medium_medium',
    lineHeight: 'body.medium_medium',
  },
});

interface SavingsPlansContentProps {
  applicationLocale: Locale;
  canceledSavingsPlansCount: number;
  translations: SavingsPlansListTranslationsKeys;
  assetId?: string;
}

const SavingsPlansContent = ({
  applicationLocale,
  translations,
  canceledSavingsPlansCount,
  assetId,
}: SavingsPlansContentProps) => {
  const { savingsPlans, status, isLoading } = useSavingsPlansContext();

  const { t } = useClientTranslation();

  const language = getLanguageFromLocale(applicationLocale) ?? defaultLanguage;

  const noSavingPlans = Boolean(savingsPlans?.edges?.length) === false;

  const count = Number(savingsPlans?.totalCount ?? 0);

  const activeSavingsPlansListTitle = t(
    getPluralTranslationKey('savings_plans_list_title_active', count, language),
  );

  const cancelledSavingsPlansListTitle = t(
    getPluralTranslationKey(
      'savings_plans_list_title_cancelled',
      count,
      language,
    ),
  );

  const allSavingsPlansListTitle =
    count > 1
      ? translations.savingsPlansListTitleAllPlural
      : translations.savingsPlansListTitleAll;

  function savingsPlansTitle() {
    if (noSavingPlans) {
      return translations.savingsPlansListTitleEmpty;
    }

    if (status === 'ACTIVE') {
      return interpolateTranslation(activeSavingsPlansListTitle, {
        count: savingsPlans?.totalCount ?? 0,
      });
    }

    if (status === 'CANCELED') {
      return interpolateTranslation(cancelledSavingsPlansListTitle, {
        count: savingsPlans?.totalCount ?? 0,
      });
    }

    return interpolateTranslation(allSavingsPlansListTitle, {
      count: savingsPlans?.totalCount ?? 0,
    });
  }

  return (
    <>
      <div
        className={css({
          paddingY: 'large',
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
        })}
      >
        <div className={titleContainerStyles}>
          {isLoading ? (
            <SavingsPlansHeaderSkeleton />
          ) : (
            <div
              className={css({
                display: 'flex',
                direction: 'row',
                justifyContent: 'space-between',
              })}
            >
              <div>
                <h1
                  className={css({
                    fontFamily: 'Inter',
                    fontSize: '28px',
                    fontStyle: 'normal',
                    fontWeight: '600',
                  })}
                >
                  {savingsPlansTitle()}
                </h1>
              </div>
              <HyperLinkButtonBase
                className={css({
                  position: {
                    base: 'fixed',
                    md: 'static',
                  },
                  bottom: {
                    base: '16px',
                  },
                  left: {
                    base: '50%',
                  },
                  transform: {
                    base: 'translateX(-50%)',
                  },
                  width: {
                    base: '342px',
                    md: '202px',
                  },
                  height: {
                    base: 'small',
                    md: '48px',
                  },

                  zIndex: {
                    base: 'docked',
                  },
                })}
                size="medium"
                visual="primary"
                to={
                  assetId
                    ? getAssetTradeUrl(assetId, {
                        type: 'savings',
                        trailingSlash: true,
                      })
                    : '/discover?next=savings'
                }
              >
                {translations.savingsPlansListCreateButton}
              </HyperLinkButtonBase>
            </div>
          )}
        </div>
        <div
          className={css({
            paddingX: 'medium',
          })}
        >
          <SavingsPlansFilterBar
            canceledSavingsPlansCount={canceledSavingsPlansCount}
            translations={translations}
          />
        </div>
      </div>

      {isLoading ? (
        <SavingsPlansItemsSkeleton />
      ) : (
        <SavingsPlansList
          applicationLocale={applicationLocale}
          savingsPlans={savingsPlans}
          translations={translations}
        />
      )}
    </>
  );
};

export { SavingsPlansContent };
